import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { getCurrencySymbol } from '@angular/common';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ApiResponse } from 'src/AngularJS/Models/api-response.model';
import { BaseGroupType } from 'src/AngularJS/Models/group-type.model';
import { Group } from 'src/AngularJS/Models/group.model';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { MetricComponent, MetricDefinition, MetricImage, NumericType } from 'src/shared/models/pipeline-config/metric-definition.models';
import { AggregationType, CurrencyFormat, MetricTypes, TimeFormat } from 'src/shared/models/pipeline-config/mongo-enums.model';
import { GroupTypes, MetricDefinitionValidation, SaveMetricResponse } from 'src/shared/services/pipeline-configuration-service-base';
import MetricComponentModalComponent from '../metric-component-modal/metric-component-modal.component';
import { FormulaState } from '../metric-formula/metric-formula.component';
import PipelineConfigurationService from '../pipeline-configuration.service';

@Component({
    selector: 'edit-metric',
    templateUrl: './edit-metric.component.html',
    styleUrls: ['./edit-metric.component.scss']
})
export class EditMetricComponent implements OnInit {

    @Input()
    definition: MetricDefinition;

    model: EditMetricViewModel;

    @Output()
    onCommit: EventEmitter<MetricDefinition> = new EventEmitter<MetricDefinition>();

    @Output()
    onCancel: EventEmitter<void> = new EventEmitter<void>();

    assignmentGroupsLoaded: boolean = false;
    assignToolData: AssignToolDataModel = { coreAssignAtLevel: 1, coreAssignAtType: "rec", rec: [] };
    assignToolOpened: boolean = false;
    groupTypes: GroupTypes = { emp: [], rec: [] };
    isBlocked: boolean = false;
    metricCategories: string[] = [];
    metricImages: MetricImage[] = [];
    metricImagesLoaded: boolean = false;
    currencies = CurrencyFormat;

    constructor(
        private cvLocalizationService: CVLocalizationService,
        private modalService: NgbModal,
        private service: PipelineConfigurationService
    ) { }

    ngOnInit() {
        this.model = new EditMetricViewModel(this.definition);
        this.loadMetricCategories();
        this.loadMetricImages();
        this.loadAssignments();
        this.loadGroupTypes();
        this.loadLocalizations();
    }

    /**
     * Loads the categories/groups of existing metrics for the Metric Category input+datalist
     */
    private async loadMetricCategories() {
        this.metricCategories = await this.service.getMetricCategories();
    }

    /**
     * Loads the metric images from the service.
     */
    private async loadMetricImages() {
        this.metricImages = await this.service.getMetricImages();
        this.metricImagesLoaded = true;
    }

    /**
     * Loads the GroupDTOs of the assignment IDs for the assign tool.  New definitions won't have any, of course.
     */
    private async loadAssignments() {
        if (this.model.metricID > 0) {
            try {
                let assignments = await this.service.getMetricAssignments(this.model.metricID);
                if (assignments.length > 0) {
                    this.assignToolData = { coreAssignAtLevel: assignments[0].GroupLevel, coreAssignAtType: "rec", rec: assignments };
                }
            } catch (ex) {
                console.error(`Could not load assignments for metric definition with metricID="${this.model.metricID}"`, ex);
            }
        }

        if (this.assignToolData == null)
            this.assignToolData = {};

        this.onAssignmentsChanged();
        this.assignmentGroupsLoaded = true;
    }

    trackAssignToolDataRec(index: number, item: Group) {
        return item.GroupID;
    }

    /**
     * Loads the GroupTypes for the current company
     * Used by the Employee and Recording Hierarchy select boxes
     */
    private async loadGroupTypes() {
        this.groupTypes = await this.service.getGroupTypes();
    }

    /**
     * Loads the Pipeline_Configuration section, since warning dialogs (like on the Cancel button) are built in the JS/TS side, not the markup.
     */
    private async loadLocalizations() {
        await this.cvLocalizationService.GetSection("Pipeline_Configuration");
    }

    /**
     * Event handler when the assignments change in the Assign Tool.
     * Updates the model with the Level and AssignmentIDs
     */
    onAssignmentsChanged(eventData?: Group[]) {
        if (eventData && eventData.length) {
            const firstGroupLevel = eventData[0].GroupLevel;
            if (eventData.every(g => g.BaseGroupType == BaseGroupType.Recording && g.GroupLevel === firstGroupLevel)) {
                this.model.assignmentLevel = firstGroupLevel;
                this.model.assignments = eventData.map(g => g.GroupID);
                this.assignToolData = {
                    coreAssignAtLevel: firstGroupLevel,
                    coreAssignAtType: "rec",
                    rec: [...eventData],
                }
            } else {
                this.model.assignmentLevel = 0;
                this.model.assignments = [];
                this.assignToolData = {
                    coreAssignAtLevel: 1,
                    coreAssignAtType: "rec",
                    rec: [],
                };
            }
        } else if (eventData && eventData.length == 0) {
            this.model.assignmentLevel = 0;
            this.model.assignments = [];
            this.assignToolData = {
                coreAssignAtLevel: 1,
                coreAssignAtType: "rec",
                rec: [],
            };
        }
        else {
            this.model.assignmentLevel = this.assignToolData?.rec?.[0]?.GroupLevel ?? 0;
            this.model.assignments = this.assignToolData?.rec?.map(g => g.GroupID) ?? [];
        }
    }

    // This boolean causes the "Min" aggregation option to be shown.  It's only changed to true when the Aggregation is already Min.
    private showAggregationMin: boolean = false;
    /**
     * Returns the value of showAggregationMin.
     * Called by ng-if on <option> for aggregation type.
     */
    shouldShowAggregationMin() {
        if (this.model.aggregationType == AggregationType.Min) {
            this.showAggregationMin = true;
        }
        return this.showAggregationMin;
    }

    /**
     * Sets the metric definition's metric image by ImageID.
     * Called by the ngClick directive on each image in the template.
     * @param $event Click event, used to see if CTRL was held when clicked.
     * @param ImageID ImageID of the metric clicked by the user.
     */
    setMetricImageID($event: MouseEvent, ImageID: number) {
        if ($event.ctrlKey && this.model.metricImage == ImageID) // deselect
            this.model.metricImage = 0;
        else
            this.model.metricImage = ImageID;
    }

    /**
     * Opens MetricComponentModal to create a new Standard or Snapshot component
     * Called by the click event on the [+ Component] button.
     */
    async addComponentClicked() {
        if (this.isBlocked || (this.model.componentType != ComponentType.Standard && this.model.componentType != ComponentType.Snapshot))
            return;
        this.isBlocked = true;
        try {
            let modalRef = this.modalService.open(MetricComponentModalComponent, {
                animation: true,
                backdrop: "static",
                backdropClass: 'stopScrolling',
                keyboard: false, // Disabled, since the modal displays a lot of configuration. Losing it on accidental ESC press isn't good.
            });

            if (this.model.componentType == ComponentType.Standard)
                (modalRef.componentInstance as MetricComponentModalComponent).metricType = MetricTypes.Standard;
            else
                (modalRef.componentInstance as MetricComponentModalComponent).metricType = MetricTypes.RealTime;

            let newComponent = await modalRef.result;

            if (this.model.componentType == ComponentType.Standard)
                this.model.standardComponents.push(newComponent);
            else
                this.model.snapshotComponents.push(newComponent);
        } catch { // Ignore non-save closes/dismisses of the modal
        } finally {
            this.isBlocked = false;
        }
    }

    /**
     * Opens MetricComponentModalComponent to edit the specified Standard Component
     * Called by onEdit event from the MetricComponentPreviewComponent
     * @param metricComponent
     */
    async editStandardComponent(metricComponent: MetricComponent) {
        if (this.isBlocked || this.model.componentType != ComponentType.Standard)
            return;
        this.isBlocked = true;

        try {
            let modalRef = this.modalService.open(MetricComponentModalComponent, {
                animation: true,
                backdrop: "static",
                backdropClass: 'stopScrolling',
                keyboard: false, // Disabled, since the modal displays a lot of configuration. Losing it on accidental ESC press isn't good.
            });

            const modalComponent = modalRef.componentInstance as MetricComponentModalComponent;
            modalComponent.metricType = MetricTypes.Standard;
            modalComponent.component = metricComponent;

            const editedMetricComponent = await modalRef.result;

            const originalIndex = this.model.standardComponents.indexOf(metricComponent);
            if (originalIndex > -1) {
                this.model.standardComponents[originalIndex] = editedMetricComponent;
            } else {
                console.error("edited component for one not found");
            }
        } catch { // Ignore non-save closes/dismisses of the modal
        } finally {
            this.isBlocked = false;
        }
    }

    /**
     * Opens MetricComponentModalComponent to edit the specified Snapshot Component
     * Called by onEdit event from the MetricComponentPreviewComponent
     * @param metricComponent
     */
    async editSnapshotComponent(metricComponent: MetricComponent) {
        if (this.isBlocked || this.model.componentType != ComponentType.Snapshot)
            return;
        this.isBlocked = true;

        try {
            let modalRef = this.modalService.open(MetricComponentModalComponent, {
                animation: true,
                backdrop: "static",
                backdropClass: 'stopScrolling',
                keyboard: false, // Disabled, since the modal displays a lot of configuration. Losing it on accidental ESC press isn't good.
            });

            const modalComponent = modalRef.componentInstance as MetricComponentModalComponent;
            modalComponent.metricType = MetricTypes.RealTime;
            modalComponent.component = metricComponent;

            const editedMetricComponent = await modalRef.result;

            const originalIndex = this.model.snapshotComponents.indexOf(metricComponent);
            if (originalIndex > -1) {
                this.model.snapshotComponents[originalIndex] = editedMetricComponent;
            } else {
                console.error("edited component for one not found");
            }
        } catch { // Ignore non-save closes/dismisses of the modal
        } finally {
            this.isBlocked = false;
        }
    }

    /**
     * Removes the Standard Metric Component from this metric definition.
     * Called by onDelete event from the MetricComponentPreviewComponent
     * @param component Reference to the component to remove
     */
    deleteStandardComponent(component: MetricComponent) {
        if (this.model.componentType != ComponentType.Standard)
            return;

        const index = this.model.standardComponents.indexOf(component);
        if (index > -1) {
            this.model.standardComponents.splice(index, 1);
        }
    }

    /**
     * Removes the Snapshot Metric Component from this metric definition.
     * Called by onDelete event from the MetricComponentPreviewComponent
     * @param component Reference to the component to remove
     */
    deleteSnapshotComponent(component: MetricComponent) {
        if (this.model.componentType != ComponentType.Snapshot)
            return;

        const index = this.model.snapshotComponents.indexOf(component);
        if (index > -1) {
            this.model.snapshotComponents.splice(index, 1);
        }
    }

    /**
     * Returns the string to use as tooltip for the Snapshot "Add Component" button.
     */
    getAddSnapshotComponentButtonTitle(): string {
        if (this.model.snapshotComponents.length > 0)
            return this.cvLocalizationService.getCachedText("Pipeline_Configuration", "Add_Snapshot_Component_Disabled", "Snapshot metrics allow only one component.");
        return this.cvLocalizationService.getCachedText("Pipeline_Configuration", "Add_Snapshot_Component_Enabled", "Add Component");
    }

    /**
     * Opens the assign tool
     * Called by the click event on the Edit Assignments button.
     */
    editAssignmentsClicked() {
        this.assignToolOpened = true;
    }

    /**
     * Returns the user to the list of metrics in the Pipeline Configuration page without saving their changes.
     * Called by ngClick directive on the Cancel and Back buttons.
     */
    public async cancelClicked() {
        if (this.isBlocked)
            return;
        this.isBlocked = true;
        try {
            await this.warnAboutLeaving();
            this.onCancel?.emit();
        } catch (ex) {
            this.isBlocked = false;
        }
    }

    /**
     * Prompts the user with the Confirm Dialog asking if they are sure they want to leave the page.
     * Returns a promise that resolves on "Yes", rejects on "No".
     */
    private warnAboutLeaving(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            let message: string =
                `<li>
                        ${this.cvLocalizationService.getCachedText("Pipeline_Configuration", "Cancel_Warning_Message", "Are you sure you want to leave? Any changes made to this metric will be lost. Continue anyway?")}
                    </li>`;
            window["showConfirmDialog"](
                message,
                [
                    {
                        text: this.cvLocalizationService.getCachedText("Pipeline_Configuration", "Yes", "Yes"),
                        click: () => {
                            (jQuery(".errorDialog") as any).dialog("close");
                            resolve();
                        }
                    },
                    {
                        text: this.cvLocalizationService.getCachedText("Pipeline_Configuration", "No", "No"),
                        click: () => {
                            (jQuery(".errorDialog") as any).dialog("close");
                            reject();
                        }
                    }
                ],
                this.cvLocalizationService.getCachedText("Pipeline_Configuration", "Cancel_Warning_Title", "Metric Validation Warning"),
                "warning"
            );
        });
    }

    /**
     * Saves the metric definition after validating input.
     * Called by ngClick directive on the Save button.
     */
    public async saveClicked() {
        if (this.isBlocked)
            return;
        this.isBlocked = true;

        if (this.isMetricDefinitionValid()) {
            try {
                let metricDefinition = this.model.toMetricDefinition();
                let saveMetricResponse = await this.service.postSaveMetricDefinition(metricDefinition);
                if (saveMetricResponse.Validation == MetricDefinitionValidation.Success) {
                    metricDefinition.MetricID = saveMetricResponse.MetricID;
                    this.service.clearMetricDefinitionCache();
                    this.onCommit?.emit(metricDefinition);
                }
            } catch (ex) {
                console.error("Error saving Metric Definition:", ex);
                // Display error dialog to the user with the ApiResponse warnings as the content (if available)
                let message = "Unknown error.";
                let response = ex as ApiResponse<SaveMetricResponse>;
                if (response?.status_code != null) {
                    message = `Status code: ${response.status_code}`;
                    if (Array.isArray(response?.warnings)) {
                        response.warnings.forEach(w => {
                            // htmlEncode() is in ClearView WebPortal/Scripts/Utilities.js
                            let sanitizedWarning: string = window["htmlEncode"](w);
                            message += `<li>${sanitizedWarning}</li>`;
                        });
                    }
                }
                window["showErrorDialog"](message, this.cvLocalizationService.getCachedText("Pipeline_Configuration", "Error_Saving_Metric", "Error Saving Metric Definition"));
                this.isBlocked = false;
            }
        } else {
            this.isBlocked = false;
        }
    }

    /**
     * Checks that the required form elements have good values.
     * This function is used by the template to enable/disable the Save button.  Validation checks for form elements.
     * Called by ngDisabled directive on the Save button.
     */
    isMetricDefinitionValid(): boolean {
        let isValid = this.model.metricName && this.model.metricName.length > 0 && this.model.metricName.length <= 50
            && this.model.metricAbv && this.model.metricAbv.length > 0 && this.model.metricAbv.length <= 20
            && this.model.category && this.model.category.length > 0 && this.model.category.length <= 50;

        if (this.model.componentType == ComponentType.Standard)
            isValid = isValid && this.model.standardComponents.length > 0;
        else if (this.model.componentType == ComponentType.Snapshot)
            isValid = isValid && this.model.snapshotComponents.length > 0;
        else if (this.model.componentType == ComponentType.Calculated)
            isValid = isValid && this.model.isFormulaValid && this.model.metricFormula.length > 0;
        else
            isValid = false;

        isValid = isValid && this.model.numericType != null;

        if (isValid && this.model.numericType == NumericType.Time) {
            switch (this.model.timeFormat) {
                case TimeFormat.Second:
                case TimeFormat.Minute:
                case TimeFormat.Hour:
                case TimeFormat.Time:
                    break;
                default:
                    isValid = false;
            }
        }
        else if(isValid && this.model.numericType == NumericType.Currency)
        {
            if(!Object.values(this.currencies).includes(this.model.currencyFormat))
                isValid=false;
        }

        // Check that the Employee and Recording Hierarchy Min Levels are valid:
        if (isValid && this.groupTypes != null) {
            isValid = isValid && (this.model.empLevelMin == null || (this.model.empLevelMin > 0 && this.model.empLevelMin <= this.groupTypes.emp.length));
            isValid = isValid && (this.model.recLevelMin == null || (this.model.recLevelMin > 0 && this.model.recLevelMin <= this.groupTypes.rec.length));
        }

        isValid = isValid && this.model.aggregationType != null;

        return isValid;
    }

    getCurrencySymbol(countryCode)
    {
        return getCurrencySymbol(countryCode, "narrow");
    }

    // This is required so that the enum is not sorted alphabetically (default)
    sortCurrency()
    {return 0;}
    
    /**
     * Updates the model variables for the calculated metric formula.
     * Called by on-formula-changed event in the MetricFormulaComponent
     * @param formulaState Event data from the MetricFormulaComponent
     */
    formulaChanged(formulaState: FormulaState) {
        this.model.metricFormula = formulaState.formulaString;
        this.model.isFormulaValid = formulaState.isValid;
    }

    // Lets me use the enum in the template markup
    get MetricTypes() {
        return MetricTypes;
    }
}


class EditMetricViewModel {
    public aggregationType: AggregationType;
    public assignmentLevel: number = 0;
    public assignments: number[] = [];
    public category: string;
    public componentType: ComponentType = ComponentType.Standard;
    public description: string;
    public direction: ObjectiveDirection = ObjectiveDirection.Up;
    public empLevelMin: number | null;
    public isFormulaValid: boolean = false;
    public markAsKPI: boolean = true; // unused for now
    public metricAbv: string;
    public metricFormula: string;
    public metricID: number; // Not editable, but still used to load assignments
    public metricImage: number;
    public metricName: string;
    public numericType: NumericType;
    public recLevelMin: number | null;
    public showInSelector: boolean;
    public snapshotComponents: MetricComponent[] = [];
    public standardComponents: MetricComponent[] = [];
    public tags: string[] = []; // unused for now
    public timeFormat: TimeFormat = null;
    public currencyFormat: CurrencyFormat = null;
    public _id: string = null;

    constructor(metricDefinition?: MetricDefinition) {
        this.metricID = metricDefinition?.MetricID ?? 0;
        this.metricAbv = metricDefinition?.Abreviation?.substring(0, 20) ?? "";
        this.metricName = metricDefinition?.MetricName?.substring(0, 50) ?? "";
        this.metricImage = metricDefinition?.MetricImageID ?? 0;
        this.category = metricDefinition?.Group?.substring(0, 50);
        this.description = metricDefinition?.MetricDescription ?? "";
        this.direction = metricDefinition?.Direction ?? ObjectiveDirection.Up;

        switch (metricDefinition?.Type) {
            case MetricTypes.RealTime:
                this.componentType = ComponentType.Snapshot;
                if (metricDefinition?.Components)
                    this.snapshotComponents = JSON.parse(JSON.stringify(metricDefinition.Components));
                break;
            case MetricTypes.Calculated:
                this.componentType = ComponentType.Calculated;
                this.metricFormula = metricDefinition?.CalculatedFormula ?? "";
                break;
            case MetricTypes.Standard:
                this.componentType = ComponentType.Standard;
                // Deep Copy the components, so edits here don't change other uses of this definition
                if (metricDefinition?.Components)
                    this.standardComponents = JSON.parse(JSON.stringify(metricDefinition.Components));
                break;
            default:
                this.componentType = undefined;
                break;
        }

        this.aggregationType = metricDefinition?.AggregationType;

        this.timeFormat = metricDefinition?.TimeFormat || undefined; // TimeFormat.None = 0, but use `undefined` instead, so it displays the empty select.
        this.currencyFormat = CurrencyFormat[metricDefinition?.CurrencyFormat] || undefined;
        this.showInSelector = metricDefinition?.ShowInSelector ?? true;
        this.numericType = metricDefinition?.NumericType;

        // Employee and Recording Hierarchy Min Level:
        if (metricDefinition == null) {
            // For new definitions, default to level 1.
            this.empLevelMin = 1;
            this.recLevelMin = 1;
        } else {
            // For existing definitions, keep their value:
            this.empLevelMin = metricDefinition.EmpLevelMin ?? undefined;
            this.recLevelMin = metricDefinition.RecLevelMin ?? undefined;
        }

        this._id = metricDefinition?._id;
    }

    /**
     * This function converts the internal model, which represents the fields in the form, to the values that a real Metric Definition expects.
     */
    public toMetricDefinition(): MetricDefinition {
        let metricType: MetricTypes;
        let components: MetricComponent[];
        let metricFormula: string;
        switch (this.componentType) {
            case ComponentType.Calculated:
                metricType = MetricTypes.Calculated;
                metricFormula = this.metricFormula;
                components = [];
                break;
            case ComponentType.Snapshot:
                metricType = MetricTypes.RealTime;
                components = this.snapshotComponents;
                break;
            default:
                metricType = MetricTypes.Standard;
                components = this.standardComponents;
                break;
        }

        let timeFormat: TimeFormat = TimeFormat.None;
        // Only copy over the timeFormat value when numericType is Time
        if (this.numericType == NumericType.Time)
            timeFormat = this.timeFormat ?? TimeFormat.None;

        let currencyFormat: string = "";
        if (this.numericType == NumericType.Currency)
            currencyFormat = Object.keys(CurrencyFormat).filter(x => CurrencyFormat[x] == this.currencyFormat)[0];

        let metricDefinition = {
            _id: this._id,
            Abreviation: this.metricAbv,
            Active: true,
            AggregationType: this.aggregationType,
            AssignAll: this.assignments.length == 0,
            AssignmentIDs: this.assignments,
            CalculatedFormula: metricFormula,
            Components: components,
            Direction: this.direction,
            EmpLevelMin: this.empLevelMin,
            Group: this.category,
            IsEditable: true,
            Level: this.assignmentLevel,
            MetricDescription: this.description,
            MetricID: this.metricID,
            MetricImageID: this.metricImage,
            MetricName: this.metricName,
            NumericType: this.numericType,
            RecLevelMin: this.recLevelMin,
            ShowInSelector: this.showInSelector,
            Status: { IsValid: true, ConsecutiveFailureCount: 0, LastSuccessDate: null },
            TimeFormat: timeFormat,
            CurrencyFormat: currencyFormat,
            Type: metricType,
        } as MetricDefinition;

        return metricDefinition;
    }
}

const enum ObjectiveDirection {
    Down = 0,
    Up = 1,
}

export const enum ComponentType {
    Standard = 0,
    Calculated = 1,
    Snapshot = 2,
}

interface AssignToolDataModel {
    coreAssignAtLevel?: number;
    coreAssignAtType?: string;
    rec?: Group[];
}
